import { storage } from "@app/config/firebase";
import {
  getDownloadURL,
  getMetadata,
  ref,
  uploadBytes,
  type UploadMetadata,
} from "firebase/storage";

export const hasFirebaseMediaToken = (url: string): boolean => {
  try {
    return Boolean(new URL(url).searchParams.get("token"));
  } catch {
    return /[?&]token=/.test(url);
  }
};

export const isFirebaseStorageUrl = (url: string): boolean => {
  try {
    const host = new URL(url).hostname;
    return (
      host === "firebasestorage.googleapis.com" ||
      host === "storage.googleapis.com" ||
      host.endsWith(".firebasestorage.app")
    );
  } catch {
    return false;
  }
};

export const extractFirebaseStoragePath = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    const match = parsed.pathname.match(/\/(?:v0\/)?b\/[^/]+\/o\/(.+)$/);
    if (match?.[1]) return decodeURIComponent(match[1]);
    return null;
  } catch {
    return null;
  }
};

export class FirebaseService {
  /** Upload and return a public download URL with `alt=media&token=...`. */
  public static uploadFileToFirebase = async (
    storagePath: string,
    file: File
  ): Promise<string> => {
    try {
      const path = storagePath.replace(/^\/+|\/+$/g, "");
      const storageRef = ref(
        storage,
        `${path}/${file.name}-${new Date().getTime()}`
      );
      const metadata: UploadMetadata = {
        contentType: file.type || undefined,
        cacheControl: "public,max-age=31536000",
      };

      const snapshot = await uploadBytes(storageRef, file, metadata);
      let downloadUrl = await getDownloadURL(snapshot.ref);

      if (!hasFirebaseMediaToken(downloadUrl)) {
        const meta = await getMetadata(snapshot.ref);
        const token =
          meta.customMetadata?.firebaseStorageDownloadTokens
            ?.split(",")[0]
            ?.trim() || "";
        if (!token) {
          throw new Error("Firebase download URL is missing media token");
        }
        const encodedPath = encodeURIComponent(
          meta.fullPath || snapshot.ref.fullPath
        );
        const bucket = meta.bucket || snapshot.ref.bucket;
        downloadUrl = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media&token=${encodeURIComponent(token)}`;
      }

      return downloadUrl;
    } catch (error) {
      console.error("Error uploading file:", error);
      throw new Error("Failed to upload file");
    }
  };
}
